import React, { useState, useMemo } from 'react';
import {
  Typography,
  Stack,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Visibility, Edit, Delete } from '@mui/icons-material';
import { useMutation, useQuery } from 'react-query';
import { capitalize } from 'lodash';

import { MainLayout } from '../components/layout/MainLayout';
import { ConfirmModal } from '../components/common/ConfirmModal';
import { contactsAPI, campaignAPI } from '../services/api';
import { useToast } from '../components/common/ToastProvider';
import { Contact } from '../services/interfaces';

// Field labels mapping
const FIELD_LABELS: Record<string, string> = {
  campaign: 'Campaign Name',
  externalId: 'External ID',
  firstName: 'First Name',
  phoneNumber: 'Phone Number',
  propertyAddress: 'Property Address',
  status: 'Status',
};

// Field order for view modal (externalId will be added at the end)
const FIELD_ORDER = [
  'firstName',
  'phoneNumber',
  'propertyAddress',
  'status',
  'campaign',
];

const EDITABLE_STATUSES = ['pending', 'paused', 'failed'];

const canEditContact = (campaignStatus?: string): boolean => {
  if (!campaignStatus) return true;
  return EDITABLE_STATUSES.includes(campaignStatus.toLowerCase());
};

const getEditDisabledTooltip = (campaignStatus?: string): string => {
  if (!campaignStatus) return 'Contact is not assigned to any campaign';
  return `Cannot edit or delete contact while campaign is ${campaignStatus}. Allowed statuses: ${EDITABLE_STATUSES.join(
    ', '
  )}`;
};

const getChangedFields = (
  original: Contact | null,
  edited: Contact | null
): { field: string; oldValue: string; newValue: string }[] => {
  if (!original || !edited) return [];
  const changes: { field: string; oldValue: string; newValue: string }[] = [];
  const fieldsToCompare: (keyof Contact)[] = [
    'firstName',
    'phoneNumber',
    'propertyAddress',
  ];

  fieldsToCompare.forEach((field) => {
    const oldVal = original[field];
    const newVal = edited[field];
    if (oldVal !== newVal) {
      changes.push({
        field: String(field),
        oldValue: oldVal === undefined ? '(empty)' : String(oldVal),
        newValue: newVal === undefined ? '(empty)' : String(newVal),
      });
    }
  });

  return changes;
};

export const ContactsPage: React.FC = () => {
  const { showToast } = useToast();

  const [page, setPage] = useState(0);
  const rowsPerPage = 20;
  const [selectedCampaignName, setSelectedCampaignName] = useState<string>('');

  // Queries
  const getContactsQuery = useQuery(
    ['contactsAPI.getContacts', page, selectedCampaignName],
    () =>
      contactsAPI.getContacts({
        page,
        campaignName: selectedCampaignName || undefined,
      }),
    {
      keepPreviousData: true,
      onError: (err) => {
        console.error(err);
        showToast('Failed to load contacts', 'error');
      },
    }
  );

  const getCampaignsForFilterQuery = useQuery(
    ['campaignAPI.getCampaignsForFilter'],
    () => campaignAPI.getCampaignsForFilter(),
    {
      onError: (err) => {
        console.error(err);
        showToast('Failed to load campaigns', 'error');
      },
    }
  );

  // Mutations
  const updateContactMutation = useMutation(
    ({ id, data }: { id: string; data: Partial<Contact> }) =>
      contactsAPI.updateContact(id, data),
    {
      onError: (err) => {
        console.error(err);
        showToast('Failed to update contact', 'error');
      },
      onSuccess: () => {
        showToast('Contact updated successfully', 'success');
        getContactsQuery.refetch();
        setConfirmUpdateOpen(false);
        setEditModalOpen(false);
        setEditContact(null);
        setOriginalContact(null);
      },
    }
  );

  const deleteContactMutation = useMutation(
    (id: string) => contactsAPI.deleteContact(id),
    {
      onError: (err) => {
        console.error(err);
        showToast('Failed to delete contact', 'error');
      },
      onSuccess: () => {
        showToast('Contact deleted successfully', 'success');
        getContactsQuery.refetch();
        setConfirmDeleteOpen(false);
        setSelectedContactId(null);
      },
    }
  );

  // View modal state
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewContact, setViewContact] = useState<Contact | null>(null);

  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editContact, setEditContact] = useState<Contact | null>(null);
  const [originalContact, setOriginalContact] = useState<Contact | null>(null);

  // Confirm modal states
  const [confirmUpdateOpen, setConfirmUpdateOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [selectedContactId, setSelectedContactId] = useState<string | null>(
    null
  );

  const handleOpenView = (contact: Contact) => {
    setViewContact(contact);
    setViewModalOpen(true);
  };

  const handleCloseView = () => {
    setViewModalOpen(false);
    setViewContact(null);
  };

  const handleOpenEdit = (contact: Contact) => {
    setOriginalContact(contact);
    setEditContact({ ...contact });
    setEditModalOpen(true);
  };

  const handleCloseEdit = () => {
    setEditModalOpen(false);
    setEditContact(null);
    setOriginalContact(null);
  };

  const handleEditChange = (field: keyof Contact, value: unknown) => {
    if (editContact) {
      setEditContact({
        ...editContact,
        [field]: value,
      });
    }
  };

  const handleUpdateClick = () => {
    setConfirmUpdateOpen(true);
  };

  const handleConfirmUpdate = () => {
    if (editContact && originalContact) {
      updateContactMutation.mutate({
        id: editContact.id,
        data: {
          firstName: editContact.firstName,
          phoneNumber: editContact.phoneNumber,
          propertyAddress: editContact.propertyAddress,
        },
      });
    }
  };

  const handleDeleteClick = (id: string) => {
    setSelectedContactId(id);
    setConfirmDeleteOpen(true);
  };

  const handleConfirmDelete = () => {
    if (selectedContactId) {
      deleteContactMutation.mutate(selectedContactId);
    }
  };

  const isFormValid = useMemo(() => {
    if (!editContact) return false;
    return (
      !!editContact.firstName &&
      !!editContact.phoneNumber &&
      !!editContact.propertyAddress
    );
  }, [editContact]);

  const changedFields = getChangedFields(originalContact, editContact);

  const renderContactsContent = () => {
    if (getContactsQuery.isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    const contacts = getContactsQuery.data?.[0] ?? [];
    const total = getContactsQuery.data?.[1] ?? 0;

    if (contacts.length === 0) {
      return (
        <Typography color='text.secondary'>No contacts available.</Typography>
      );
    }

    return (
      <>
        <TableContainer
          component={Paper}
          variant='outlined'
        >
          <Table size='small'>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'grey.100' }}>
                <TableCell>
                  <Typography variant='subtitle2'>First Name</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant='subtitle2'>Campaign Name</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant='subtitle2'>Campaign Status</Typography>
                </TableCell>
                <TableCell align='right'>
                  <Typography variant='subtitle2'>Actions</Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {contacts.map((contact) => {
              const campaignStatus = contact.campaign?.status;
              const canEdit = canEditContact(campaignStatus);
              const editTooltip = canEdit
                ? 'Edit'
                : getEditDisabledTooltip(campaignStatus);

              return (
                <TableRow
                  key={contact.id}
                  hover
                >
                  <TableCell>{contact.firstName}</TableCell>
                  <TableCell>{contact.campaign?.name || '-'}</TableCell>
                  <TableCell>{capitalize(campaignStatus) || '-'}</TableCell>
                  <TableCell align='right'>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 1,
                      }}
                    >
                      <Tooltip title='View'>
                        <IconButton
                          size='small'
                          onClick={() => handleOpenView(contact)}
                          color='primary'
                        >
                          <Visibility fontSize='small' />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={canEdit ? 'Edit' : editTooltip}>
                        <span>
                          <IconButton
                            size='small'
                            onClick={() => handleOpenEdit(contact)}
                            color='primary'
                            disabled={!canEdit}
                          >
                            <Edit fontSize='small' />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={canEdit ? 'Delete' : editTooltip}>
                        <span>
                          <IconButton
                            size='small'
                            onClick={() => handleDeleteClick(contact.id)}
                            color='error'
                            disabled={
                              !canEdit || deleteContactMutation.isLoading
                            }
                          >
                            <Delete fontSize='small' />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component='div'
        count={total}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[]}
        showFirstButton
        showLastButton
      />
    </>
    );
  };

  const renderFieldValue = (contact: Contact, key: string): React.ReactNode => {
    const value = (contact as unknown as Record<string, unknown>)[key];
    if (key === 'campaign') {
      return (value as { name: string })?.name || '-';
    }
    if (value === undefined || value === null || value === '') {
      return <span style={{ color: '#888' }}>(empty)</span>;
    }
    return String(value);
  };

  return (
    <MainLayout>
      <Stack
        spacing={4}
        width='100%'
      >
        <Typography
          variant='h4'
          gutterBottom
        >
          Contacts
        </Typography>

        <FormControl
          size='small'
          sx={{ minWidth: 240, alignSelf: 'flex-start' }}
        >
          <InputLabel>Campaign Filter</InputLabel>
          <Select
            value={selectedCampaignName}
            label='Campaign Filter'
            onChange={(e) => {
              setSelectedCampaignName(e.target.value as string);
              setPage(0);
            }}
          >
            <MenuItem value=''>
              <em>All Campaigns</em>
            </MenuItem>
            {(getCampaignsForFilterQuery.data || []).map((campaign: any) => (
              <MenuItem
                key={campaign.name}
                value={campaign.name}
              >
                {campaign.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {renderContactsContent()}
      </Stack>

      {/* View Modal */}
      <Dialog
        open={viewModalOpen}
        onClose={handleCloseView}
        maxWidth='md'
        fullWidth
      >
        <DialogTitle>View Contact</DialogTitle>
        <DialogContent>
          {viewContact && (
            <Paper
              variant='outlined'
              sx={{
                p: 2,
                backgroundColor: 'grey.50',
                fontFamily: 'monospace',
              }}
            >
              {/* Regular fields */}
              {FIELD_ORDER.map((key) => (
                <Box
                  key={key}
                  sx={{ mb: 2 }}
                >
                  <Typography
                    variant='subtitle2'
                    sx={{
                      fontFamily: 'monospace',
                      fontWeight: 600,
                      color: 'primary.main',
                    }}
                  >
                    {FIELD_LABELS[key]}:
                  </Typography>
                  <Typography
                    variant='body2'
                    sx={{
                      fontFamily: 'monospace',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      pl: 1,
                    }}
                  >
                    {renderFieldValue(viewContact, key)}
                  </Typography>
                </Box>
              ))}

              {/* External ID at the bottom */}
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant='subtitle2'
                  sx={{
                    fontFamily: 'monospace',
                    fontWeight: 600,
                    color: 'primary.main',
                  }}
                >
                  {FIELD_LABELS.externalId}:
                </Typography>
                <Typography
                  variant='body2'
                  sx={{
                    fontFamily: 'monospace',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    pl: 1,
                  }}
                >
                  {renderFieldValue(viewContact, 'externalId')}
                </Typography>
              </Box>
            </Paper>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseView}
            variant='contained'
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Modal */}
      <Dialog
        open={editModalOpen}
        onClose={handleCloseEdit}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Edit Contact</DialogTitle>
        <DialogContent>
          {editContact && (
            <Stack
              spacing={2}
              sx={{ mt: 1 }}
            >
              <TextField
                fullWidth
                required
                label='First Name'
                value={editContact.firstName}
                onChange={(e) => handleEditChange('firstName', e.target.value)}
                variant='outlined'
                size='small'
              />

              <TextField
                fullWidth
                required
                label='Phone Number'
                value={editContact.phoneNumber}
                onChange={(e) =>
                  handleEditChange('phoneNumber', e.target.value)
                }
                variant='outlined'
                size='small'
              />

              <TextField
                fullWidth
                required
                label='Property Address'
                value={editContact.propertyAddress}
                onChange={(e) =>
                  handleEditChange('propertyAddress', e.target.value)
                }
                variant='outlined'
                size='small'
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseEdit}
            disabled={updateContactMutation.isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateClick}
            variant='contained'
            disabled={
              updateContactMutation.isLoading ||
              !isFormValid ||
              changedFields.length === 0
            }
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Update Modal */}
      <ConfirmModal
        open={confirmUpdateOpen}
        title='Confirm Update'
        onCancel={() => setConfirmUpdateOpen(false)}
        onConfirm={handleConfirmUpdate}
        isLoading={updateContactMutation.isLoading}
        confirmText='Update'
      >
        <Stack spacing={1}>
          <Typography
            variant='body1'
            gutterBottom
          >
            Are you sure you want to update this contact? The following changes
            will be applied:
          </Typography>
          {changedFields.map((change) => (
            <Paper
              key={change.field}
              variant='outlined'
              sx={{ p: 1.5, backgroundColor: 'grey.50' }}
            >
              <Typography
                variant='subtitle2'
                color='primary'
                gutterBottom
              >
                {FIELD_LABELS[change.field] || change.field}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography
                  variant='body2'
                  color='text.secondary'
                >
                  <strong>Old:</strong> {change.oldValue}
                </Typography>
                <Typography variant='body2'>
                  <strong>New:</strong> {change.newValue}
                </Typography>
              </Box>
            </Paper>
          ))}
        </Stack>
      </ConfirmModal>

      {/* Confirm Delete Modal */}
      <ConfirmModal
        open={confirmDeleteOpen}
        title='Delete Contact'
        onCancel={() => {
          setConfirmDeleteOpen(false);
          setSelectedContactId(null);
        }}
        onConfirm={handleConfirmDelete}
        isLoading={deleteContactMutation.isLoading}
        confirmText='Delete'
        confirmColor='error'
      >
        Are you sure you want to delete this contact? This action cannot be
        undone.
      </ConfirmModal>
    </MainLayout>
  );
};
